/**
 * CropEditorDrawer — entity-specific drawer для «Культуры» (AK-231).
 *
 * Тонкая обвязка над ReferenceCatalogDrawer + EditorSection: 5 секций по
 * wireframe (Идентификация / Визуал / Классификация / Агрохарактеристики /
 * Фенофазы). Бизнес-валидации:
 *   - code: уникальный, /^[a-z0-9_]+$/.
 *   - name_translations: ≥1 непустой язык (FR-2).
 *   - category_id: обязателен при СОЗДАНИИ (FR-2). AK-504 — в edit mode НЕ
 *     блокирует save: 15/84 активных культур на staging (legacy
 *     `agroai-<uuid>` импорт) без category_id, и правка любого другого поля
 *     такой культуры немедленно триггерила disabled Save + красную ошибку
 *     «Выберите категорию» без пути сохранить несвязанную правку — founder
 *     UAT 31.07 «редактировать культуру — выдаёт ошибку». Тот же паттерн, что
 *     AK-269 уже применил к `code` (см. ниже) — грандфазерим legacy-данные в
 *     edit, не ослабляя правило для create.
 *   - tkw: либо оба null, либо min ≤ max (FR-3).
 *   - color: валидный hex.
 */
import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, ChevronDown, ChevronUp, Copy, Link2, Plus, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  EditorSection,
  ReferenceCatalogDrawer,
} from '@/components/reference/ReferenceCatalogDrawer';
import { ColorPicker, isValidHex } from '@/components/reference/ColorPicker';
import { NameTranslationsField } from '@/components/reference/NameTranslationsField';
import { MultiSelectChips } from '@/components/reference/MultiSelectChips';
import { describeApiError } from '@/lib/apiError';
import { toast } from '@/lib/toast';
import type {
  Crop,
  CropCategory,
  CropCreatePayload,
  GrowingGoal,
  Phenophase,
  PhenophaseScale,
} from '@/pages/platform-admin/reference/__mocks__/reference';
import type { CropGroup } from '@/pages/platform-admin/reference/types';
import {
  useCreatePhenophase,
  useCrop,
  useDeletePhenophase,
  usePhenophases,
  useReorderPhenophases,
} from '@/pages/platform-admin/reference/hooks/useReferenceQueries';

/**
 * Sentinel для опциональных Radix-Select'ов (group_id / season_phase): Radix
 * `SelectItem` запрещает пустую строку как value, поэтому «не задано»
 * мапим на этот непечатаемый sentinel ↔ '' в FormState.
 */
const NONE_VALUE = '__none__';

/** AK-259 — нормализованный сезон-enum (совпадает с DB CHECK + season_phase API). */
export const SEASON_PHASE_OPTIONS: { value: Exclude<SeasonPhase, ''>; label: string }[] = [
  { value: 'winter', label: 'Озимый' },
  { value: 'spring', label: 'Яровой' },
  { value: 'wintering', label: 'Зимующий' },
];

/** AK-259 — нормализованный сезон-enum (level-2 таксономия). */
export type SeasonPhase = 'winter' | 'spring' | 'wintering' | '';

export interface FormState {
  code: string;
  name_translations: Record<string, string>;
  latin_name: string;
  color: string;
  category_id: string;
  /** AK-259 — группа культур (level 2, опционально). '' = не задано. */
  group_id: string;
  /** AK-259 — нормализованный сезон. '' = не задано. */
  season_phase: SeasonPhase;
  growing_goal_ids: string[];
  is_winter: boolean;
  is_perennial: boolean;
  tkw_min: string;
  tkw_max: string;
  sowing_season: 'winter' | 'spring' | 'winter_or_spring' | '';
  default_seeding_rate: string;
}

const EMPTY_FORM: FormState = {
  code: '',
  name_translations: { ru: '' },
  latin_name: '',
  color: '#9CA3AF',
  category_id: '',
  group_id: '',
  season_phase: '',
  growing_goal_ids: [],
  is_winter: false,
  is_perennial: false,
  tkw_min: '',
  tkw_max: '',
  sowing_season: '',
  default_seeding_rate: '',
};

function cropToForm(c: Crop): FormState {
  return {
    // AK-273 — code опционален; бескодовая культура → пустой инпут (не null).
    code: c.code ?? '',
    name_translations: { ...c.name_translations },
    // Миграция 111 — если латынь ПРИШЛА ОТ БИОЛОГИЧЕСКОЙ культуры, в форму её
    // не кладём: иначе первый же сейв зафиксировал бы копию на товарной и
    // разорвал наследование (правка латыни у «Пшеницы» перестала бы доезжать).
    latin_name: c.latin_name_inherited ? '' : (c.latin_name ?? ''),
    color: c.color,
    category_id: c.category_id ?? '',
    group_id: c.group_id ?? '',
    season_phase: c.season_phase ?? '',
    growing_goal_ids: [...c.growing_goal_ids],
    is_winter: c.is_winter,
    is_perennial: c.is_perennial,
    // AK-260 — wire/detail отдаёт канонические имена БД; UI-local FormState
    // держит короткие (tkw_min/max, default_seeding_rate) для читаемости формы.
    tkw_min: c.thousand_seeds_weight_min_g == null ? '' : String(c.thousand_seeds_weight_min_g),
    tkw_max: c.thousand_seeds_weight_max_g == null ? '' : String(c.thousand_seeds_weight_max_g),
    sowing_season: c.sowing_season ?? '',
    default_seeding_rate:
      c.default_seeding_rate_min == null ? '' : String(c.default_seeding_rate_min),
  };
}

interface FormErrors {
  name?: string;
  category?: string;
  color?: string;
  tkw?: string;
}

export function validate(f: FormState, mode: 'create' | 'edit' = 'create'): FormErrors {
  const errs: FormErrors = {};
  const hasName = Object.values(f.name_translations).some((v) => v.trim().length > 0);
  if (!hasName) errs.name = 'Требуется минимум 1 язык';
  // AK-504 — категория обязательна только при создании. В edit грандфазерим
  // legacy-импорт без category_id: иначе правка ЛЮБОГО другого поля такой
  // культуры блокировала Save недостижимой ошибкой (см. комментарий выше).
  if (mode === 'create' && !f.category_id) errs.category = 'Выберите категорию';
  if (!isValidHex(f.color)) errs.color = 'Формат #RRGGBB';
  const min = f.tkw_min ? Number(f.tkw_min) : null;
  const max = f.tkw_max ? Number(f.tkw_max) : null;
  if (min !== null && max !== null && min > max) {
    errs.tkw = `Минимум (${min}) не может быть больше максимума (${max})`;
  }
  return errs;
}

function toPayload(f: FormState): CropCreatePayload {
  const trimmedTrans: Record<string, string> = {};
  for (const [k, v] of Object.entries(f.name_translations)) {
    if (v.trim()) trimmedTrans[k] = v.trim();
  }
  return {
    // AK-273 — пустой/пробельный code шлём как null (зеркально бэку: ""→NULL),
    // иначе вторая бескодовая культура ловит UniqueViolation.
    code: f.code.trim() || null,
    name_translations: trimmedTrans,
    latin_name: f.latin_name.trim() || null,
    color: f.color,
    // AK-504 — edit mode может сохранять legacy-культуру без категории;
    // backend CropBase.category_id: uuid.UUID | None уже принимает null,
    // но НЕ принимает '' (422 uuid_parsing) — пустая строка запрещена.
    category_id: f.category_id === '' ? null : f.category_id,
    group_id: f.group_id === '' ? null : f.group_id,
    season_phase: f.season_phase === '' ? null : f.season_phase,
    growing_goal_ids: f.growing_goal_ids,
    is_winter: f.is_winter,
    is_perennial: f.is_perennial,
    // AK-260 — отправляем канонические имена, которые принимает backend CropIn.
    thousand_seeds_weight_min_g: f.tkw_min ? Number(f.tkw_min) : null,
    thousand_seeds_weight_max_g: f.tkw_max ? Number(f.tkw_max) : null,
    sowing_season: f.sowing_season === '' ? null : f.sowing_season,
    default_seeding_rate_min: f.default_seeding_rate ? Number(f.default_seeding_rate) : null,
  };
}

export interface CropEditorDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  crop: Crop | null;
  categories: CropCategory[];
  /** AK-259 — список групп культур для select «Группа культур». */
  groups: CropGroup[];
  goals: GrowingGoal[];
  onSubmit: (payload: CropCreatePayload) => Promise<void>;
  /**
   * AK-239: pre-filled form values для clone-сценария («Создать на основе»).
   * Применяется только в `mode === 'create'`. Если не задано — используется
   * EMPTY_FORM. Передаётся parent'ом, который сначала строит override из
   * cropToForm(sourceCrop), сбрасывает code+name и переключает state.
   */
  initialOverride?: Partial<FormState> | null;
  /**
   * AK-239: callback на кнопку «Создать на основе» в шапке drawer'а. Виден
   * только в `mode === 'edit'` (когда есть исходник для клонирования).
   * Parent сам решает что делать (как правило: построить override + сбросить
   * editingCrop → переключение в create-with-prefill).
   */
  onCloneClick?: (source: Crop) => void;
}

export function CropEditorDrawer({
  open,
  onOpenChange,
  mode,
  crop,
  categories,
  groups,
  goals,
  onSubmit,
  initialOverride,
  onCloneClick,
}: CropEditorDrawerProps) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  /**
   * AK-259 (B1-lesson) — при edit подтягиваем detail культуры (group_id /
   * season_phase / growing_goal_ids / tkw / sowing_season — source of truth).
   * Detail заполняет форму поверх list-item: пока detail грузится, форма
   * стартует из переданного `crop`, затем обновляется когда detail приедет.
   */
  const detailQ = useCrop(open && mode === 'edit' && crop ? crop.id : null);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && crop) {
      // Baseline из list-item — мгновенно (форма не пустая пока грузится detail).
      // Detail догидратирует форму в отдельном эффекте ниже.
      setForm(cropToForm(crop));
    } else if (mode === 'create' && initialOverride) {
      // AK-239: clone — стартуем с override (всё кроме code+name из source).
      setForm({ ...EMPTY_FORM, ...initialOverride });
      // Если override задал name_translations — оставляем, иначе пустой объект.
      // Touched=true чтобы юзер сразу увидел требование заполнить code+name.
      setTouched(true);
      return;
    } else {
      setForm(EMPTY_FORM);
    }
    setTouched(false);
  }, [open, mode, crop, initialOverride]);

  /**
   * AK-259 (B1-lesson) — когда detail приехал, перезаполняем форму его данными
   * (source of truth), НО только если юзер ещё не правил форму (touched=false),
   * иначе late-detail затёр бы правки. detail.id-guard защищает от гонки при
   * быстром переключении между культурами.
   */
  useEffect(() => {
    if (!open || mode !== 'edit' || !crop) return;
    const detail = detailQ.data;
    if (!detail || detail.id !== crop.id) return;
    if (touched) return;
    setForm(cropToForm(detail));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, crop, detailQ.data]);

  const errors = useMemo(() => validate(form, mode), [form, mode]);
  const isInvalid = Object.keys(errors).length > 0;
  const showErrors = touched;

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    setTouched(true);
  };

  const handleSave = async () => {
    setTouched(true);
    if (isInvalid) return;
    setSaving(true);
    try {
      await onSubmit(toPayload(form));
      onOpenChange(false);
    } catch (err) {
      // AK-574 — отказ сервера пользователю, а не только в консоль.
      toast.error(describeApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ReferenceCatalogDrawer
      open={open}
      onOpenChange={onOpenChange}
      mode={mode}
      entityLabel="культуры"
      headerSlot={
        mode === 'edit' && crop ? (
          <span className="flex items-center gap-2 text-sm">
            <span
              className="inline-block h-4 w-4 rounded border border-border"
              style={{ backgroundColor: crop.color }}
              aria-hidden
            />
            <span className="font-medium text-foreground">
              {crop.name_translations.ru ?? crop.code}
            </span>
            <span className="font-mono text-xs text-muted-foreground">
              {crop.code}
            </span>
          </span>
        ) : null
      }
      headerActionSlot={
        mode === 'edit' && crop && onCloneClick ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onCloneClick(crop)}
            disabled={saving}
            title="Создать новую культуру с полями текущей (кроме кода и названия)"
          >
            <Copy className="h-3.5 w-3.5" />
            Создать на основе
          </Button>
        ) : null
      }
      saveDisabled={showErrors && isInvalid}
      saveLoading={saving}
      onSave={handleSave}
    >
      {/* 1. Identification */}
      <EditorSection number={1} title="Идентификация">
        <div className="mb-3 grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="crop-code">Код</Label>
            <Input
              id="crop-code"
              value={form.code}
              onChange={(e) => set('code', e.target.value)}
              placeholder="wheat_winter"
              className="mt-1 font-mono text-sm"
              disabled={mode === 'edit'}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Необязательный. Не меняется после создания.
            </p>
          </div>
          <div>
            <Label htmlFor="crop-latin">Латинское название</Label>
            <Input
              id="crop-latin"
              value={form.latin_name}
              onChange={(e) => set('latin_name', e.target.value)}
              placeholder={crop?.latin_name_inherited ? (crop.latin_name ?? '') : 'Triticum aestivum'}
              className="mt-1"
            />
            {crop?.latin_name_inherited && (
              <p className="mt-1 text-xs text-muted-foreground">
                {inheritedHint(crop, 'латынь')}
              </p>
            )}
          </div>
        </div>
        <Label className="mb-1.5 block">
          Название (мультиязык) <span className="text-destructive">*</span>
        </Label>
        <NameTranslationsField
          value={form.name_translations}
          onChange={(v) => {
            setForm((f) => ({ ...f, name_translations: v }));
            setTouched(true);
          }}
          locales={['ru', 'en']}
          hint={showErrors && errors.name ? errors.name : 'Требуется минимум 1 язык.'}
          hasError={showErrors && Boolean(errors.name)}
        />
      </EditorSection>

      {/* 2. Visual */}
      <EditorSection number={2} title="Визуал">
        <Label className="mb-2 block">Цвет заливки на карте</Label>
        <ColorPicker
          value={form.color}
          onChange={(v) => set('color', v)}
        />
        <p className="mt-2 text-xs text-muted-foreground">
          По умолчанию <code>#9CA3AF</code>. Цвет применяется к слою культуры
          на <code>/map</code> у всех хозяйств.
        </p>
      </EditorSection>

      {/* 3. Classification */}
      <EditorSection number={3} title="Классификация">
        <div className="mb-3 grid grid-cols-2 gap-3">
          <div>
            <Label>
              Товарная группа {mode === 'create' && <span className="text-destructive">*</span>}
            </Label>
            <Select
              value={form.category_id || undefined}
              onValueChange={(v) => set('category_id', v)}
            >
              <SelectTrigger
                className={cn(
                  'mt-1',
                  showErrors && errors.category && 'border-destructive',
                )}
              >
                <SelectValue placeholder="Выберите…" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name_translations.ru ?? c.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {showErrors && errors.category && (
              <p className="mt-1 text-xs text-destructive">{errors.category}</p>
            )}
            {/* AK-504 — legacy-импорт (agroai-*) без категории: не блокируем
                save, но подсказываем, что классификация не заполнена. */}
            {mode === 'edit' && !form.category_id && !(showErrors && errors.category) && (
              <p className="mt-1 text-xs text-muted-foreground">
                Категория не задана (импортированная культура). Можно сохранить
                как есть или выбрать категорию.
              </p>
            )}
          </div>
          <div>
            <Label>Биологическая культура</Label>
            <Select
              value={form.group_id || NONE_VALUE}
              onValueChange={(v) => set('group_id', v === NONE_VALUE ? '' : v)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VALUE}>— не задана</SelectItem>
                {groups.map((g) => (
                  <SelectItem key={g.id} value={g.id}>
                    {g.name_translations.ru ?? g.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">
              «Пшеница» для озимой и яровой. Отсюда наследуются латынь,
              ботаника, фенофазы и кислотность почвы.
            </p>
          </div>
        </div>
        <div className="mb-3 grid grid-cols-2 gap-3">
          <div>
            <Label>Сезон</Label>
            <Select
              value={form.season_phase || NONE_VALUE}
              onValueChange={(v) =>
                set('season_phase', v === NONE_VALUE ? '' : (v as SeasonPhase))
              }
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VALUE}>— не задан</SelectItem>
                {SEASON_PHASE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">
              Нормализованный сезон (озимый / яровой / зимующий).
            </p>
          </div>
          <div />
        </div>
        <Label className="mb-1.5 block">Цели выращивания</Label>
        <MultiSelectChips
          options={goals.map((g) => ({
            value: g.id,
            label: g.name_translations.ru ?? g.code,
          }))}
          selected={form.growing_goal_ids}
          onChange={(v) => set('growing_goal_ids', v)}
          placeholder="Выберите цели…"
        />

        {mode === 'edit' && crop && <BiologyPanel crop={crop} />}

        <div className="mt-4 divide-y divide-border rounded-md border border-border">
          <ToggleRow
            label="Озимая"
            hint="is_winter — сев под зиму"
            checked={form.is_winter}
            onChange={(v) => set('is_winter', v)}
          />
          <ToggleRow
            label="Многолетняя"
            hint="is_perennial — растёт несколько сезонов"
            checked={form.is_perennial}
            onChange={(v) => set('is_perennial', v)}
          />
        </div>
      </EditorSection>

      {/* 4. Agro */}
      <EditorSection number={4} title="Агрохарактеристики">
        <Label className="mb-1.5 block">Масса 1000 семян (TKW), г</Label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Input
              type="number"
              value={form.tkw_min}
              onChange={(e) => set('tkw_min', e.target.value)}
              placeholder="мин"
              className={cn(showErrors && errors.tkw && 'border-destructive')}
            />
            <p className="mt-1 text-xs text-muted-foreground">минимум</p>
          </div>
          <div>
            <Input
              type="number"
              value={form.tkw_max}
              onChange={(e) => set('tkw_max', e.target.value)}
              placeholder="макс"
              className={cn(showErrors && errors.tkw && 'border-destructive')}
            />
            <p className="mt-1 text-xs text-muted-foreground">максимум</p>
          </div>
        </div>
        {showErrors && errors.tkw && (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-destructive">
            <AlertCircle className="h-3.5 w-3.5" />
            {errors.tkw}
          </div>
        )}

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div>
            <Label>Сезон сева</Label>
            <Select
              value={form.sowing_season || undefined}
              onValueChange={(v) =>
                set('sowing_season', v as FormState['sowing_season'])
              }
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="winter">Озимый</SelectItem>
                <SelectItem value="spring">Яровой</SelectItem>
                <SelectItem value="winter_or_spring">Озимый / яровой</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="crop-seeding">Норма высева, кг/га</Label>
            <Input
              id="crop-seeding"
              type="number"
              value={form.default_seeding_rate}
              onChange={(e) => set('default_seeding_rate', e.target.value)}
              className="mt-1"
            />
            <p className="mt-1 text-xs text-muted-foreground">default_seeding_rate</p>
          </div>
        </div>
      </EditorSection>

      {/* 5. Phenophases */}
      <EditorSection
        number={5}
        title="Фенофазы"
        hint="сгруппированы по шкале"
      >
        {mode === 'create' ? (
          <p className="rounded-md bg-secondary/50 px-3 py-2 text-xs text-muted-foreground">
            Сначала сохраните культуру — тогда станут доступны фенофазы.
          </p>
        ) : (
          crop && <PhenophasesEditor cropId={crop.id} />
        )}
      </EditorSection>

      {/* 6. Provenance (read-only) */}
      {mode === 'edit' && crop && (
        <EditorSection number={6} title="Синхронизация">
          <div className="space-y-2">
            <div>
              <Label className="text-muted-foreground">Источник</Label>
              <p className="mt-0.5 flex items-center gap-1.5 text-sm text-foreground">
                <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
                {crop.external_agrohistory_id ? 'АгроИстория' : '—'}
              </p>
            </div>
            <div>
              <Label className="text-muted-foreground">Внешний ID</Label>
              <p className="mt-0.5 font-mono text-xs text-muted-foreground">
                {crop.external_agrohistory_id ?? '—'}
              </p>
            </div>
          </div>
        </EditorSection>
      )}
    </ReferenceCatalogDrawer>
  );
}

/**
 * Подпись «унаследовано от <биологическая культура>» (миграция 111 / ADR-044 §4).
 * Показывается там, где значение пришло НЕ с товарной культуры, чтобы админ
 * понимал: править надо на карточке биологической культуры, а введённое здесь
 * значение станет переопределением.
 */
export function inheritedHint(crop: Pick<Crop, 'group_name'>, what: string): string {
  const from = crop.group_name ? `от «${crop.group_name}»` : 'от биологической культуры';
  return `Сейчас ${what} наследуется ${from}. Заполните поле, чтобы задать своё значение.`;
}

/** Строки блока «Биология» карточки товарной культуры. */
const BIO_ROWS: { label: string; value: (c: Crop) => string; inherited: (c: Crop) => boolean }[] = [
  {
    label: 'Семейство',
    value: (c) => c.botanical_family ?? '—',
    inherited: (c) => Boolean(c.botanical_family_inherited),
  },
  {
    label: 'Род',
    value: (c) => c.botanical_genus ?? '—',
    inherited: (c) => Boolean(c.botanical_genus_inherited),
  },
  {
    label: 'Класс',
    value: (c) => c.botanical_class ?? '—',
    inherited: (c) => Boolean(c.botanical_class_inherited),
  },
  {
    label: 'Шкала фенофаз',
    value: (c) => c.phenophase_scale ?? '—',
    inherited: (c) => Boolean(c.phenophase_scale_inherited),
  },
  {
    label: 'Кислотность почвы (pH)',
    value: (c) =>
      c.soil_ph_min == null && c.soil_ph_max == null
        ? '—'
        : `${c.soil_ph_min ?? '—'} — ${c.soil_ph_max ?? '—'}`,
    inherited: (c) => Boolean(c.soil_ph_min_inherited || c.soil_ph_max_inherited),
  },
];

/**
 * Read-only сводка биологических свойств товарной культуры: что своё, что
 * унаследовано от биологической. Правятся они на карточке биологической
 * культуры (/platform-admin/reference/crop-groups) — здесь только видно.
 */
function BiologyPanel({ crop }: { crop: Crop }) {
  return (
    <div className="mt-4 rounded-md border border-border">
      <div className="border-b border-border bg-secondary/40 px-3 py-2 text-xs font-semibold">
        Биология
      </div>
      <dl className="divide-y divide-border">
        {BIO_ROWS.map((row) => (
          <div key={row.label} className="flex items-center justify-between gap-3 px-3 py-2">
            <dt className="text-xs text-muted-foreground">{row.label}</dt>
            <dd className="flex items-center gap-2 text-sm text-foreground">
              {row.value(crop)}
              {row.inherited(crop) && (
                <Badge variant="secondary" className="uppercase tracking-wide text-muted-foreground">
                  унаследовано
                </Badge>
              )}
            </dd>
          </div>
        ))}
      </dl>
      <p className="px-3 py-2 text-xs text-muted-foreground">
        Правятся на карточке биологической культуры — там значение сразу
        применяется ко всем её товарным культурам.
      </p>
    </div>
  );
}

function ToggleRow({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between px-3 py-2.5">
      <div>
        <div className="text-sm font-medium text-foreground">{label}</div>
        <div className="text-xs text-muted-foreground">{hint}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

// ----------------------------------------------------------------------------
// Phenophases sub-editor (per-crop, BBCH/Zadoks/other groups)
// ----------------------------------------------------------------------------

const SCALE_OPTIONS: { value: PhenophaseScale; label: string }[] = [
  { value: 'BBCH', label: 'BBCH' },
  { value: 'Zadoks', label: 'Zadoks' },
  { value: 'other', label: 'Другое' },
];

interface AddDraft {
  code: string;
  name_ru: string;
  display_order: string;
}

function emptyAddDraft(): AddDraft {
  return { code: '', name_ru: '', display_order: '' };
}

function PhenophasesEditor({ cropId }: { cropId: string }) {
  const { data, isLoading } = usePhenophases(cropId);
  const createMut = useCreatePhenophase();
  const deleteMut = useDeletePhenophase();
  const reorderMut = useReorderPhenophases();

  const [drafts, setDrafts] = useState<Record<PhenophaseScale, AddDraft>>({
    BBCH: emptyAddDraft(),
    Zadoks: emptyAddDraft(),
    other: emptyAddDraft(),
  });

  const grouped = useMemo<Record<PhenophaseScale, Phenophase[]>>(() => {
    const out: Record<PhenophaseScale, Phenophase[]> = {
      BBCH: [],
      Zadoks: [],
      other: [],
    };
    for (const p of data?.items ?? []) {
      out[p.scale] = [...out[p.scale], p];
    }
    for (const s of SCALE_OPTIONS) {
      out[s.value].sort((a, b) => a.display_order - b.display_order);
    }
    return out;
  }, [data]);

  /**
   * AK-240: переместить фазу вверх/вниз внутри её scale.
   *
   * `direction = -1` — вверх (swap с предыдущей), `+1` — вниз. Reorder
   * атомарный: вызываем PUT /order с новым массивом id всей scale, бэкенд
   * проставит display_order = 1..N в одной транзакции. Тем самым избегаем
   * "two-step swap" race condition и unique-constraint conflict'ов.
   */
  const handleMove = async (
    scale: PhenophaseScale,
    index: number,
    direction: -1 | 1,
  ) => {
    const items = grouped[scale];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= items.length) return;
    const next = [...items];
    const [moved] = next.splice(index, 1);
    next.splice(targetIndex, 0, moved);
    try {
      await reorderMut.mutateAsync({
        cropId,
        order: next.map((p) => p.id),
      });
    } catch (err) {
      toast.error(describeApiError(err));
    }
  };

  const handleAdd = async (scale: PhenophaseScale) => {
    const d = drafts[scale];
    if (!d.code.trim() || !d.name_ru.trim()) return;
    const nextOrder = grouped[scale].length + 1;
    const order = d.display_order ? Number(d.display_order) : nextOrder;
    try {
      await createMut.mutateAsync({
        cropId,
        payload: {
          scale,
          code: d.code.trim(),
          name_translations: { ru: d.name_ru.trim() },
          display_order: order,
        },
      });
      setDrafts((s) => ({ ...s, [scale]: emptyAddDraft() }));
    } catch (err) {
      // Дубль кода фазы (409) и битый display_order (422) — на экран.
      toast.error(describeApiError(err));
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  // Миграция 111 — своих фаз нет, показываем фазы биологической культуры.
  const allInherited =
    (data?.items.length ?? 0) > 0 && (data?.items ?? []).every((p) => p.inherited);

  return (
    <div className="space-y-3">
      {allInherited && (
        <p className="rounded-md bg-secondary/50 px-3 py-2 text-xs text-muted-foreground">
          Фазы показаны от биологической культуры — у этой товарной культуры
          своих нет. Добавьте фазу ниже, чтобы задать собственный набор.
        </p>
      )}
      {SCALE_OPTIONS.map((s) => {
        const items = grouped[s.value];
        const d = drafts[s.value];
        return (
          <div key={s.value} className="overflow-hidden rounded-md border border-border">
            <div className="flex items-center justify-between bg-secondary/40 px-3 py-2 text-xs font-semibold">
              <span>Шкала {s.label}</span>
              <span className="font-normal text-muted-foreground">
                {items.length === 0
                  ? 'нет фаз'
                  : items[0].inherited
                    ? `${items.length} фаз · унаследованы`
                    : `${items.length} фаз`}
              </span>
            </div>
            {items.length > 0 && (
              <div className="divide-y divide-border">
                <div className="grid grid-cols-[70px_1fr_56px_28px_28px_36px] gap-2 bg-secondary/20 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <span>Код</span>
                  <span>Название</span>
                  <span>Порядок</span>
                  <span />
                  <span />
                  <span />
                </div>
                {items.map((p, idx) => {
                  const isFirst = idx === 0;
                  const isLast = idx === items.length - 1;
                  // Миграция 111 — фаза принадлежит сестре по биологической
                  // культуре. Reorder вернёт 400 (чужой crop_id), а DELETE
                  // снёс бы чужую фазу → строка read-only.
                  const readOnly = Boolean(p.inherited);
                  return (
                    <div
                      key={p.id}
                      className={cn(
                        'grid grid-cols-[70px_1fr_56px_28px_28px_36px] items-center gap-2 px-3 py-2 text-xs',
                        readOnly && 'text-muted-foreground',
                      )}
                    >
                      <span className="font-mono">{p.code}</span>
                      <span>{p.name_translations.ru ?? '—'}</span>
                      <span className="tabular-nums">{p.display_order}</span>
                      <button
                        type="button"
                        onClick={() => {
                          void handleMove(s.value, idx, -1);
                        }}
                        disabled={readOnly || isFirst || reorderMut.isPending}
                        className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
                        aria-label="Поднять выше"
                      >
                        <ChevronUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          void handleMove(s.value, idx, 1);
                        }}
                        disabled={readOnly || isLast || reorderMut.isPending}
                        className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
                        aria-label="Опустить ниже"
                      >
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          deleteMut.mutate(
                            { cropId, phenophaseId: p.id },
                            {
                              onError: (err) =>
                                toast.error(describeApiError(err)),
                            },
                          )
                        }
                        disabled={readOnly}
                        title={readOnly ? 'Унаследованная фаза — правится у биологической культуры' : 'Удалить фазу'}
                        className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
                        aria-label="Удалить фазу"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="grid grid-cols-[70px_1fr_56px_28px_28px_36px] items-center gap-2 border-t border-border bg-secondary/30 px-3 py-2">
              <Input
                value={d.code}
                onChange={(e) =>
                  setDrafts((st) => ({
                    ...st,
                    [s.value]: { ...d, code: e.target.value },
                  }))
                }
                placeholder="код"
                className="h-7 font-mono text-xs"
              />
              <Input
                value={d.name_ru}
                onChange={(e) =>
                  setDrafts((st) => ({
                    ...st,
                    [s.value]: { ...d, name_ru: e.target.value },
                  }))
                }
                placeholder="название фазы"
                className="h-7 text-xs"
              />
              <Input
                value={d.display_order}
                onChange={(e) =>
                  setDrafts((st) => ({
                    ...st,
                    [s.value]: { ...d, display_order: e.target.value },
                  }))
                }
                placeholder="№"
                className="h-7 text-xs"
                type="number"
              />
              <Button
                size="icon"
                variant="ghost"
                onClick={() => handleAdd(s.value)}
                disabled={!d.code.trim() || !d.name_ru.trim() || createMut.isPending}
                aria-label="Добавить фазу"
                className="col-span-3 h-7 w-7 justify-self-end text-primary"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
